import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PolicyModal } from '../components/PolicyModal';
import { FloatingCTA } from '../components/FloatingCTA';
type PolicyType = 'privacy' | 'terms' | 'refund';
export function LegSwellingAdvertorial() {
  const [policyModal, setPolicyModal] = useState<{
    isOpen: boolean;
    type: PolicyType;
  }>({
    isOpen: false,
    type: 'privacy'
  });
  const openPolicy = (type: PolicyType) => {
    setPolicyModal({
      isOpen: true,
      type
    });
  };
  const closePolicy = () => {
    setPolicyModal({
      ...policyModal,
      isOpen: false
    });
  };
  return <div className="min-h-screen bg-white" data-id="element-2573">
      {/* Header */}
      <header className="bg-white border-b border-bone-dark" data-id="element-2574">
        <div className="max-w-4xl mx-auto px-4 py-2" data-id="element-2575">
          <Link to="/" className="flex items-center" data-id="element-2576">
            <img src="https://cdn.magicpatterns.com/uploads/483EwVYaDBT86YSQWmyKfo/GINEXT_LOGO.png" alt="GINEXT" className="h-6 w-auto" loading="eager" data-id="element-2577" />
          </Link>
        </div>
      </header>

      {/* Article Container */}
      <article className="max-w-3xl mx-auto px-4 py-6" data-id="element-2578">
        {/* Article Header */}
        <div className="mb-6" data-id="element-2579">
          <h1 className="text-2xl md:text-3xl font-bold text-charcoal leading-tight mb-3" data-id="element-2580">
            I Thought My Legs Were Just Tired — Until I Realized They Were
            Holding Pressure
          </h1>

          <p className="text-sm text-charcoal-light mb-3" data-id="element-2581">
            By late afternoon, my legs didn't hurt exactly.
          </p>

          <p className="text-sm text-charcoal-light mb-3" data-id="element-2582">
            They just felt… heavier.
          </p>
        </div>

        {/* Article Content */}
        <div className="prose prose-sm max-w-none" data-id="element-2583">
          <div className="space-y-3 text-charcoal leading-relaxed" data-id="element-2584">
            <p className="text-sm" data-id="element-2585">
              Shoes fit tighter. Ankles looked thicker. When I took my socks
              off, the marks stayed longer than they used to.
            </p>

            <p className="text-sm" data-id="element-2586">
              I told myself it was normal. Long days. Sitting too much. Getting
              older.
            </p>

            <p className="text-sm font-semibold" data-id="element-2587">
              But here's what bothered me most:
            </p>

            <p className="text-sm" data-id="element-2588">No pain cream ever helped.</p>

            <p className="text-sm" data-id="element-2589">
              Cold gels. Menthol rubs. "Extra strength" formulas. They all did
              the same thing — made my skin feel numb, then wore off.
            </p>

            <p className="text-sm font-semibold" data-id="element-2590">The heaviness stayed.</p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2591">
              Why Swelling Feels So Hard to Fix
            </h2>

            <p className="text-sm" data-id="element-2592">Most people treat leg swelling like pain.</p>

            <p className="text-sm font-semibold" data-id="element-2593">
              But swelling isn't a sensation problem. It's a movement problem.
            </p>

            <p className="text-sm" data-id="element-2594">
              When circulation slows — from long days, inactivity, stress, or
              time — fluid doesn't move the way it should.
            </p>

            <p className="text-sm" data-id="element-2595">So it stays.</p>

            <p className="text-sm" data-id="element-2596">
              That's why numbing creams don't work. They don't move anything.
              They just distract the nerves.
            </p>

            <p className="text-sm" data-id="element-2597">
              And compression helps… but only while it's on.
            </p>

            <p className="text-sm font-semibold" data-id="element-2598">
              I wanted something that worked with my body — not against it.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2599">
              The Moment That Changed How I Thought About Relief
            </h2>

            <p className="text-sm" data-id="element-2600">A friend said something simple:</p>

            <div className="bg-bone/50 border-l-4 border-ginger p-3 rounded-r-lg my-4" data-id="element-2601">
              <p className="text-base font-bold text-charcoal m-0" data-id="element-2602">
                "Cold shuts things down. Warmth invites things to move."
              </p>
            </div>

            <p className="text-sm" data-id="element-2603">That stuck.</p>

            <p className="text-sm" data-id="element-2604">
              Our bodies associate warmth with circulation. With relaxation.
              With release.
            </p>

            <p className="text-sm" data-id="element-2605">
              So instead of trying another freezing gel, I tried something
              completely different — a botanical oil designed to create gentle,
              controlled warmth.
            </p>

            <p className="text-sm" data-id="element-2606">
              Not heat. Not burn. Just enough warmth to signal circulation.
            </p>

            <p className="text-sm font-semibold" data-id="element-2607">That's when things changed.</p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2608">
              What I Noticed First
            </h2>

            <p className="text-sm" data-id="element-2609">There was no shock.</p>

            <p className="text-sm" data-id="element-2610">No icy blast. No aggressive tingle.</p>

            <p className="text-sm" data-id="element-2611">
              Just a slow warmth that spread instead of attacking.
            </p>

            <p className="text-sm" data-id="element-2612">
              And over time — not instantly, but consistently — my legs felt
              lighter.
            </p>

            <p className="text-sm" data-id="element-2613">Not numb. Not masked.</p>

            <p className="text-sm font-semibold" data-id="element-2614">Supported.</p>

            <p className="text-sm" data-id="element-2615">
              The tightness eased. The end-of-day heaviness didn't build the
              same way.
            </p>

            <p className="text-sm font-semibold" data-id="element-2616">
              For the first time, relief didn't feel like a trick.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2617">
              Why This Works When Creams Don't
            </h2>

            <div className="mb-4 rounded-lg overflow-hidden shadow-lg" data-id="element-2618">
              <img src="https://cdn.magicpatterns.com/uploads/miu3veA8hGZj2xhtF4swUi/u8896349413_An_educational_anatomical_illustration_showing_mu_40b13d86-9c0d-4e08-a9b7-380274aef750_3.png" alt="Anatomical illustration showing circulation" className="w-full h-auto" loading="lazy" data-id="element-2619" />
            </div>

            <p className="text-sm" data-id="element-2620">
              Most topical products are designed to block sensation.
            </p>

            <p className="text-sm font-semibold" data-id="element-2621">This oil works differently.</p>

            <p className="text-sm" data-id="element-2622">
              It uses specific botanicals — like ginger and black pepper — known
              for encouraging circulation through warmth.
            </p>

            <p className="text-sm" data-id="element-2623">
              Not synthetic heat. Not artificial numbing.
            </p>

            <p className="text-sm" data-id="element-2624">
              Just a signal the body already understands.
            </p>

            <div className="bg-bone/50 border-l-4 border-ginger p-3 rounded-r-lg my-4" data-id="element-2625">
              <p className="text-base font-bold text-charcoal m-0" data-id="element-2626">
                When circulation improves, fluid can move again. When fluid
                moves, pressure can release.
              </p>
            </div>

            <p className="text-sm" data-id="element-2627">That's the part most products ignore.</p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2628">
              "This Doesn't Feel Like Pain Cream"
            </h2>

            <p className="text-sm" data-id="element-2629">
              That's the phrase I keep hearing from people who try it.
            </p>

            <p className="text-sm" data-id="element-2630">
              Because it doesn't feel like something overpowering your body.
            </p>

            <p className="text-sm font-semibold" data-id="element-2631">
              It feels like something giving your body permission to do what it
              already knows how to do.
            </p>

            <p className="text-sm" data-id="element-2632">People use it on:</p>

            <ul className="space-y-1 my-3" data-id="element-2633">
              <li className="text-sm" data-id="element-2634">Ankles and feet that swell by evening</li>
              <li className="text-sm" data-id="element-2635">Lower legs that feel heavy or tight</li>
              <li className="text-sm" data-id="element-2636">
                Knees and joints that feel stiff after long days
              </li>
              <li className="text-sm" data-id="element-2637">
                Areas where cold products just don't make sense anymore
              </li>
            </ul>

            <p className="text-sm" data-id="element-2638">
              And because it absorbs cleanly, there's no greasy residue, no
              overpowering scent, and no waiting around.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2639">
              The Bigger Shift
            </h2>

            <p className="text-sm" data-id="element-2640">
              What surprised me most wasn't just how my legs felt.
            </p>

            <p className="text-sm" data-id="element-2641">It was how my mindset changed.</p>

            <p className="text-sm" data-id="element-2642">I stopped thinking:</p>

            <p className="text-sm italic" data-id="element-2643">"How do I shut this feeling off?"</p>

            <p className="text-sm" data-id="element-2644">And started thinking:</p>

            <p className="text-sm italic" data-id="element-2645">"How do I support my body better?"</p>

            <p className="text-sm font-semibold" data-id="element-2646">
              That's a very different relationship with relief.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2647">
              The Product Behind the Experience
            </h2>

            <div className="mb-4 rounded-lg overflow-hidden shadow-lg" data-id="element-2648">
              <img src="https://cdn.magicpatterns.com/uploads/rCeUo34cwDxa2NpcRLnUx1/GINEXT_BOTTLE_.png" alt="GINEXT Ginger Oil bottle" className="w-full h-auto" loading="lazy" data-id="element-2649" />
            </div>

            <p className="text-sm" data-id="element-2650">The oil is called GINEXT.</p>

            <p className="text-sm" data-id="element-2651">
              It's a 100% natural botanical formulation designed to:
            </p>

            <ul className="space-y-1 my-3" data-id="element-2652">
              <li className="text-sm" data-id="element-2653">Create deep, controlled warmth</li>
              <li className="text-sm" data-id="element-2654">
                Support circulation beneath the surface
              </li>
              <li className="text-sm" data-id="element-2655">Avoid numbing or freezing sensations</li>
            </ul>

            <p className="text-sm" data-id="element-2656">
              No menthol overload. No harsh chemicals. No gimmicks.
            </p>

            <p className="text-sm" data-id="element-2657">
              Just something that fits into real life — morning or evening,
              daily or as needed.
            </p>

            <h2 className="text-xl font-bold text-charcoal mt-6 mb-3" data-id="element-2658">
              If This Sounds Familiar
            </h2>

            <p className="text-sm" data-id="element-2659">If your legs:</p>

            <ul className="space-y-1 my-3" data-id="element-2660">
              <li className="text-sm" data-id="element-2661">Feel heavier as the day goes on</li>
              <li className="text-sm" data-id="element-2662">Look more swollen by evening</li>
              <li className="text-sm" data-id="element-2663">Don't respond to cold creams</li>
              <li className="text-sm" data-id="element-2664">Feel tight rather than "painful"</li>
            </ul>

            <p className="text-sm font-semibold" data-id="element-2665">
              This approach may make more sense than what you've already tried.
            </p>

            <div className="bg-white border border-bone-dark rounded-lg p-3 my-4" data-id="element-2666">
              <p className="text-base italic text-charcoal m-0" data-id="element-2667">
                "This doesn't feel like pain cream. It feels like my body
                letting go."
              </p>
            </div>

            {/* CTA Section */}
            <div className="bg-bone rounded-xl p-4 my-6 text-center" data-id="element-2668">
              <h3 className="text-lg font-bold text-charcoal mb-2" data-id="element-2669">
                Learn how GINEXT supports natural circulation and leg comfort
              </h3>
              <div className="flex flex-col sm:flex-row gap-2 justify-center items-center mt-3" data-id="element-2670">
                <Link to="/#bundles" data-id="element-2671">
                  <motion.button whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} className="bg-ginger text-white px-5 py-2.5 rounded-lg font-bold text-sm shadow-warm hover:shadow-warm-hover transition-all duration-300 inline-flex items-center gap-2" data-id="element-2672">
                    See How GINEXT Works
                    <ArrowRight className="w-4 h-4" data-id="element-2673" />
                  </motion.button>
                </Link>
              </div>

              <p className="text-xs text-charcoal-light mt-3 italic" data-id="element-2674">
                30-day money-back guarantee. No pressure.
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-charcoal text-gray-400 py-4 mt-8 text-center text-xs" data-id="element-2675">
        <div className="max-w-4xl mx-auto px-4" data-id="element-2676">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-3" data-id="element-2677">
            <button onClick={() => openPolicy('privacy')} className="hover:text-ginger transition-colors underline" data-id="element-2678">
              Privacy Policy
            </button>
            <button onClick={() => openPolicy('terms')} className="hover:text-ginger transition-colors underline" data-id="element-2679">
              Terms of Service
            </button>
            <button onClick={() => openPolicy('refund')} className="hover:text-ginger transition-colors underline" data-id="element-2680">
              Refund Policy
            </button>
          </div>
          <p data-id="element-2681">© {new Date().getFullYear()} GINEXT. All rights reserved.</p>
          <p className="mt-1" data-id="element-2682">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease.
          </p>
        </div>
      </footer>

      <PolicyModal isOpen={policyModal.isOpen} onClose={closePolicy} type={policyModal.type} data-id="element-2683" />

      <FloatingCTA ctaText="Support Natural Leg Circulation" ctaLink="/#bundles" subText="Warming botanical relief • 30-Day Guarantee" data-id="element-2684" />
    </div>;
}